// ─────────────────────────────────────────────────────────────────────────────
// How an NJ Transit bus moves between GPS fixes: drawn where it should be NOW, not where it
// was when it last reported.
//
// Framework-free, like railModel, so it is unit-tested.
//
// ── Why not the railroads' glide ──
// `advanceRailGlide` takes each fix as a destination and spends RAIL_GLIDE_S getting there.
// That ignores when the fix was taken, so a drawn bus reached a position ~30 s after it
// arrived, and the position was already 5–60 s old on arrival (NJ Transit's publish lag
// plus our poll). Measured: every bus drawn 40–90 s behind itself — two to three blocks for
// a local, over a mile for an express on the Turnpike.
//
// So a bus here is PREDICTED: the fix projected onto the route's drawn line, plus the bus's
// speed along that line times the fix's age (by the server's clock — see serverClock.ts).
// Speed is measured along the line between successive fixes, seeded by the backend's
// straight-line figure until there are two.
//
// ── When the GPS goes quiet ──
// A fix older than NJ_BUS_STALE_S is no longer a position to predict from. Two cases have
// something better than holding it (both decided by `njBusPositionKind`):
//   • LAYING OVER — the backend sets `layoverUntil` while a bus sits at its trip's first stop
//     before the trip leaves. That fix is true however old it is (a covered terminal like
//     Port Authority blocks GPS, it doesn't move the bus), so the bus is held ON it.
//   • ESTIMATED — the backend attaches the trip's upcoming calls to a quiet bus, and the bus
//     is carried along them on its timetable. The delay at the fix is carried forward: the
//     bus was at the fix at `fixAt`, the timetable says it should have been there at some
//     time t, and every later call is shifted by `fixAt − t`. Never shifted EARLY — a bus
//     running early is drawn on time, because a bus drawn ahead of itself is the one a rider
//     misses. Estimation runs until the fix is NJ_BUS_ESTIMATE_MAX_AGE_S old and then holds:
//     a bus that stopped reporting that long ago may have broken down or gone out of
//     service, and carrying it on down the road would be inventing it.
// Neither applies when there is no line to project onto, or no calls; the bus is then held at
// the end of its prediction, a last sighting.
//
// ── Catching up: the subway's rule ──
// The drawn bus FOLLOWS its target (the prediction, the layover point or the estimate) with
// the subway's critically-damped SmoothDamp (transitMapTracks: PROGRESS_SMOOTH_TIME,
// MAX_CATCHUP_MPS): a bus behind its target speeds up to close the gap, its on-screen speed
// is always continuous, and the close rate is capped at a physical speed, so a fresh fix far
// ahead of an estimate is a brief faster-than-normal run, never a dart across the map.
//
// ── Never backwards ──
// A prediction overshoots whenever the bus stops — at a stop, a light, in traffic — and the
// next fix lands behind where it is drawn. Sliding the bus back reads as reversing, so it
// HOLDS instead: it waits where it is until the target comes back past it. Two exceptions,
// both real changes of mind rather than corrections:
//   • the bus turned round (the fix moved NJ_BUS_REVERSE_M against its direction), and then
//     it travels forward in its new direction — only on a line not known to be its own
//     direction's; on one that is, a turnaround is a new trip and moves it to that trip's lines;
//   • the target was grossly wrong (the drawn bus is NJ_BUS_REWIND_M past it): it is
//     re-placed, since waiting minutes for reality to catch up is a worse lie than a jump.
//
// ── Which line ──
// A route carries lines PER DIRECTION, each digitized the way its buses travel, because on a
// divided road the two directions are different roads — the Lincoln Tunnel's north tube is
// westbound, its south tube eastbound. A bus is moved along a line of its own trip's
// direction, forwards (`ownDirection`); one with no known direction takes the line its bearing
// runs along. Running a bus backwards along the other direction's line is what drew westbound
// buses through the eastbound tube, and it is now only the last resort, for a route the feed
// shapes one way only.
//
// A fix off every drawn line of its route (a detour, a deadhead, GPS noise) has no line to
// predict along, so it keeps the old behaviour: a straight glide to the fix.
// ─────────────────────────────────────────────────────────────────────────────

import {
  RAIL_SNAP_M,
  RAIL_TRACK_KEEP_M,
  RAIL_TRACK_SNAP_M,
  bearingDeg,
  dirFromBearing,
  metresBetween,
  pointAtDistance,
  projectOnPath,
  type RailPath,
} from './railModel'
import { MAX_CATCHUP_MPS, PROGRESS_SMOOTH_TIME } from './transitMapTracks'

/** How far past its fix a bus is predicted, at most. Past this a fix is a bus that has
 *  stopped reporting, and guessing further only drives it off down the road. */
export const NJ_BUS_PREDICT_MAX_S = 60
/** A fix older than this is STALE: no longer predicted from. The bus is held (laying over),
 *  estimated from its timetable, or left as a last sighting — see njBusPositionKind. */
export const NJ_BUS_STALE_S = 120
/** A stale bus is carried along its timetable until its fix is this old (five minutes of
 *  estimating), and then held where the estimate left it. */
export const NJ_BUS_ESTIMATE_MAX_AGE_S = NJ_BUS_STALE_S + 5 * 60
/** No bus is predicted faster than this, ~67 mph — a GPS jump is not a speed. */
export const NJ_BUS_MAX_SPEED_MPS = 30
/** Weight of the newest along-line speed sample against the running estimate. */
const SPEED_BLEND = 0.7
/** Moving this far against its direction between fixes is a turnaround, not GPS wander. */
export const NJ_BUS_REVERSE_M = 60
/** Drawn this far past its target, a bus is re-placed instead of held. */
export const NJ_BUS_REWIND_M = 300
/** Off-line buses glide straight to a fix over this long (the railroads' figure). */
const FREE_GLIDE_S = 30
const FREE_HEADING_TAU_S = 1.5
/** Calls closer together than this along the line are one place (a stop listed twice, or two
 *  stops projecting onto the same point): the later is dropped. */
const CALL_MIN_GAP_M = 1

/** One upcoming call of a quiet bus's trip, resolved to a place: epoch seconds, server clock. */
export interface NjBusCall {
  lon: number
  lat: number
  arr: number
  dep: number
}

export interface NjBusFix {
  lon: number
  lat: number
  bearing: number
  /** When the fix was taken, epoch seconds by the SERVER's clock. Absent → when it arrived. */
  at?: number
  /** The backend's straight-line speed between this bus's last two fixes, m/s. */
  speedMps?: number
  /** Parked at its trip's first stop until this time (VehiclePosition.layoverUntil). */
  layoverUntil?: number
  /** The trip's calls from the last one passed (VehiclePosition.upcomingCalls, resolved to
   *  coordinates). Held by identity: pass the SAME array while the payload is unchanged, and
   *  the projection onto the line is not redone each frame. */
  calls?: readonly NjBusCall[]
}

/** What a drawn NJ Transit bus position IS. */
export type NjBusPositionKind =
  /** A fresh fix, predicted forward. */
  | 'live'
  /** Parked at its trip's first stop before departure, on its (possibly old) fix. */
  | 'layover'
  /** No fresh fix: carried along the trip's timetable. */
  | 'estimated'
  /** No fresh fix for longer than NJ_BUS_ESTIMATE_MAX_AGE_S: held where the estimate ended. */
  | 'lastEstimate'
  /** No fresh fix and nothing to estimate from: its last reported position. */
  | 'lastSighting'

/**
 * The rule shared by the map and the vehicle sheet. `estimable` is whether the bus has calls
 * to be carried along (the map also needs the bus on a drawn line, which the sheet can't see).
 */
export function njBusPositionKind(
  fixAt: number | undefined,
  nowSec: number,
  { layoverUntil, estimable }: { layoverUntil?: number; estimable: boolean },
): NjBusPositionKind {
  if (layoverUntil !== undefined && nowSec < layoverUntil) return 'layover'
  if (fixAt === undefined || nowSec - fixAt <= NJ_BUS_STALE_S) return 'live'
  if (!estimable) return 'lastSighting'
  return nowSec - fixAt <= NJ_BUS_ESTIMATE_MAX_AGE_S ? 'estimated' : 'lastEstimate'
}

/** A quiet bus's timeline along its line, in FORWARD metres (`d × dir`), delay applied. The
 *  first point is the fix itself; each later one is a call ahead of it. */
interface Timeline {
  s: number[]
  arr: number[]
  dep: number[]
}

export interface NjBusMotion {
  /** The route line being followed, or null when gliding free. */
  path: RailPath | null
  /** +1 travelling towards increasing `d`, −1 towards decreasing. */
  dir: 1 | -1
  /** The latest fix: distance along `path`, and when it was taken. */
  fixD: number
  fixAt: number
  /** Along-line speed estimate, m/s, never negative. */
  speed: number
  fixKey: string
  // Free-glide endpoints (path === null).
  fromLon: number
  fromLat: number
  toLon: number
  toLat: number
  t0: number
  t1: number
  // The drawn pose now.
  d: number
  lon: number
  lat: number
  heading: number
  /** The drawn bus's forward speed along the line, m/s — the SmoothDamp's velocity state. */
  vel: number
  /** The fix is older than NJ_BUS_STALE_S. */
  stale: boolean
  /** What the drawn position is. */
  kind: NjBusPositionKind
  /** The estimate's timeline, and what it was built from (rebuilt when any of them changes). */
  timeline: Timeline | null
  timelineFrom: { calls: readonly NjBusCall[]; path: RailPath; dir: 1 | -1; fixKey: string } | null
}

const clampSpeed = (v: number | undefined) =>
  v !== undefined && Number.isFinite(v) ? Math.max(0, Math.min(NJ_BUS_MAX_SPEED_MPS, v)) : 0

/** Where the prediction puts the bus along its line at `nowSec`. */
function predictedD(m: NjBusMotion, path: RailPath, nowSec: number): number {
  const age = Math.max(0, Math.min(NJ_BUS_PREDICT_MAX_S, nowSec - m.fixAt))
  return Math.max(0, Math.min(path.length, m.fixD + m.dir * m.speed * age))
}

/** The timeline's time at forward distance `s`: dwelling at a call ends at its departure. */
function timeAt(tl: { s: number[]; arr: number[]; dep: number[] }, s: number): number {
  const n = tl.s.length
  if (s <= tl.s[0]) return s < tl.s[0] ? tl.arr[0] : tl.dep[0]
  for (let i = 0; i < n - 1; i++) {
    if (s < tl.s[i + 1]) {
      const f = (s - tl.s[i]) / (tl.s[i + 1] - tl.s[i])
      return tl.dep[i] + f * (tl.arr[i + 1] - tl.dep[i])
    }
    if (s === tl.s[i + 1]) return tl.dep[i + 1]
  }
  return tl.dep[n - 1]
}

/** The timeline's forward distance at time `t`, clamped to its ends. */
function distanceAt(tl: Timeline, t: number): number {
  const n = tl.s.length
  if (t <= tl.dep[0]) return tl.s[0]
  for (let i = 0; i < n - 1; i++) {
    if (t < tl.arr[i + 1]) {
      const span = tl.arr[i + 1] - tl.dep[i]
      const f = span > 0 ? (t - tl.dep[i]) / span : 1
      return tl.s[i] + f * (tl.s[i + 1] - tl.s[i])
    }
    if (t <= tl.dep[i + 1]) return tl.s[i + 1]
  }
  return tl.s[n - 1]
}

/**
 * A quiet bus's timeline: its calls projected onto its line, then re-timed to the fix. Null
 * when fewer than one call lies ahead of the fix on the line.
 */
function buildTimeline(m: NjBusMotion, path: RailPath, calls: readonly NjBusCall[]): Timeline | null {
  // The calls on the line, in forward order. Skipping a call that does not move forward drops
  // stops a loop route projects back onto an earlier stretch.
  const raw = { s: [] as number[], arr: [] as number[], dep: [] as number[] }
  for (const c of calls) {
    const p = projectOnPath(path, c.lon, c.lat)
    if (p.offM > RAIL_TRACK_SNAP_M) continue
    const s = p.d * m.dir
    const last = raw.s.length - 1
    if (last >= 0 && (s <= raw.s[last] + CALL_MIN_GAP_M || c.arr < raw.dep[last])) continue
    raw.s.push(s)
    raw.arr.push(c.arr)
    raw.dep.push(Math.max(c.arr, c.dep))
  }
  if (raw.s.length < 2) return null
  // Re-timed to the fix: the bus was at sFix at fixAt; the timetable had it there at tFix.
  // Late → every later call shifts late by as much. Early → drawn on time.
  const sFix = m.fixD * m.dir
  const tFix = timeAt(raw, sFix)
  const late = Math.max(0, m.fixAt - tFix)
  const tl: Timeline = { s: [sFix], arr: [tFix + late], dep: [tFix + late] }
  for (let i = 0; i < raw.s.length; i++) {
    if (raw.s[i] <= sFix + CALL_MIN_GAP_M) continue
    tl.s.push(raw.s[i])
    tl.arr.push(raw.arr[i] + late)
    tl.dep.push(raw.dep[i] + late)
  }
  return tl.s.length >= 2 ? tl : null
}

/**
 * The line a fix belongs on, preferring one that runs WITH the bus. Every line is digitized in
 * its own direction of travel, so on a divided road the line running the bus's way is the
 * road it is on, even when the other direction's is nearer (GPS noise, a tunnel's tubes).
 * With `ownDirection` the lines are already the bus's own direction, and nearest wins.
 *
 * Unlike railModel's `pickPath`, the current line is only kept while it is still among
 * `paths`: when a bus's trip turns round, its direction's lines are a different set.
 */
function pickNjBusPath(
  paths: readonly RailPath[],
  current: RailPath | null,
  lon: number,
  lat: number,
  bearing: number,
  ownDirection: boolean,
): { path: RailPath; d: number } | null {
  const runsWith = (path: RailPath, d: number) => ownDirection || dirFromBearing(path, d, bearing) === 1
  if (current && paths.includes(current)) {
    const p = projectOnPath(current, lon, lat)
    if (p.offM <= RAIL_TRACK_KEEP_M && runsWith(current, p.d)) return { path: current, d: p.d }
  }
  let nearest: { path: RailPath; d: number; off: number } | null = null
  let nearestWith: { path: RailPath; d: number; off: number } | null = null
  for (const path of paths) {
    const p = projectOnPath(path, lon, lat)
    if (p.offM > RAIL_TRACK_SNAP_M) continue
    if (!nearest || p.offM < nearest.off) nearest = { path, d: p.d, off: p.offM }
    if (runsWith(path, p.d) && (!nearestWith || p.offM < nearestWith.off)) nearestWith = { path, d: p.d, off: p.offM }
  }
  // Nothing runs its way (a route the feed only shapes one way): the nearest line, travelled
  // backwards — the best there is.
  const best = nearestWith ?? nearest
  return best && { path: best.path, d: best.d }
}

/**
 * Advance (or start) a bus's motion to `nowSec` (server clock, seconds), taking `fix` as its
 * latest report and `paths` as its route's lines. An existing state is updated IN PLACE.
 *
 * `ownDirection`: `paths` are the lines of the bus's own trip direction (its `directionRef`),
 * so the bus travels each one FORWARD — read off the data, not off a bearing that is 0 when
 * NJ Transit sends none. Otherwise `paths` are all the route's lines, and the bus's bearing
 * picks between them.
 */
export function advanceNjBusMotion(
  m: NjBusMotion | undefined,
  fix: NjBusFix,
  paths: readonly RailPath[],
  nowSec: number,
  dtSec: number,
  ownDirection = false,
): NjBusMotion {
  const stamped = fix.at !== undefined && Number.isFinite(fix.at)
  const at = stamped ? fix.at! : nowSec
  // Keyed on the fix's own time, not `at`: an unstamped fix must not look new every frame.
  const key = `${stamped ? fix.at : ''}|${fix.lat.toFixed(6)},${fix.lon.toFixed(6)}`
  const bearing = Number.isFinite(fix.bearing) ? fix.bearing : 0

  const place = (): NjBusMotion => {
    const hit = pickNjBusPath(paths, null, fix.lon, fix.lat, bearing, ownDirection)
    const dir = !hit || ownDirection ? 1 : dirFromBearing(hit.path, hit.d, bearing)
    const s: NjBusMotion = {
      path: hit?.path ?? null, dir, fixD: hit?.d ?? 0, fixAt: at, speed: clampSpeed(fix.speedMps), fixKey: key,
      fromLon: fix.lon, fromLat: fix.lat, toLon: fix.lon, toLat: fix.lat, t0: nowSec, t1: nowSec,
      d: hit?.d ?? 0, lon: fix.lon, lat: fix.lat, heading: bearing, vel: 0, stale: false, kind: 'live',
      timeline: null, timelineFrom: null,
    }
    // First sight: straight to the target — there is no drawn bus yet to glide from.
    if (s.path) s.d = targetD(s, s.path, fix, nowSec)
    return s
  }

  if (!m) return settle(place(), fix, nowSec, dtSec, bearing)

  // A new fix — or the bus's lines changed under it (its trip turned round at a terminal).
  if (key !== m.fixKey || (m.path && !paths.includes(m.path))) {
    if (metresBetween(m.lon, m.lat, fix.lon, fix.lat) > RAIL_SNAP_M) {
      return settle(Object.assign(m, place()), fix, nowSec, dtSec, bearing)
    }
    const hit = pickNjBusPath(paths, m.path, fix.lon, fix.lat, bearing, ownDirection)
    if (hit && hit.path === m.path) {
      // Same line: measure the bus's speed along it since the last fix.
      const moved = (hit.d - m.fixD) * m.dir
      const dt = at - m.fixAt
      if (moved < -NJ_BUS_REVERSE_M && !ownDirection) {
        // Turned round. Forward is now the other way, so the drawn bus travels to the
        // prediction rather than being held short of it. Not on a line of the bus's own
        // direction: that line only runs one way, and a bus that really turned round is on a
        // new trip, whose lines are another set (the bus is re-picked onto them).
        m.dir = m.dir === 1 ? -1 : 1
        m.speed = dt > 0 ? clampSpeed(-moved / dt) : 0
        m.vel = 0
      } else if (dt > 0) {
        const sample = clampSpeed(Math.max(0, moved) / dt)
        m.speed = m.speed * (1 - SPEED_BLEND) + sample * SPEED_BLEND
      }
      m.fixD = hit.d
    } else if (hit) {
      // A different line of the route (a branch, or the other direction's shape): carry the
      // drawn bus across and read direction off where it has to go.
      const from = projectOnPath(hit.path, m.lon, m.lat).d
      const moved = hit.d - from
      m.dir = ownDirection ? 1
        : Math.abs(moved) >= 8 ? (moved > 0 ? 1 : -1) : dirFromBearing(hit.path, hit.d, bearing)
      Object.assign(m, { path: hit.path, d: from, fixD: hit.d, vel: 0 })
      if (fix.speedMps !== undefined) m.speed = clampSpeed(fix.speedMps)
    } else {
      m.path = null
      m.speed = 0
      m.vel = 0
      m.fromLon = m.lon
      m.fromLat = m.lat
      m.toLon = fix.lon
      m.toLat = fix.lat
      m.t0 = nowSec
      m.t1 = nowSec + FREE_GLIDE_S
    }
    m.fixAt = at
    m.fixKey = key
  }
  return settle(m, fix, nowSec, dtSec, bearing)
}

/** Where the bus should be along its line now — and, as a side effect, what kind of position
 *  that is (`m.kind`) and the estimate's timeline, rebuilt only when its inputs change. */
function targetD(m: NjBusMotion, path: RailPath, fix: NjBusFix, nowSec: number): number {
  const kind = njBusPositionKind(m.fixAt, nowSec, {
    layoverUntil: fix.layoverUntil,
    estimable: !!fix.calls && fix.calls.length >= 2,
  })
  if (kind === 'layover') {
    m.kind = kind
    return m.fixD
  }
  if (kind === 'estimated' || kind === 'lastEstimate') {
    const from = m.timelineFrom
    if (!from || from.calls !== fix.calls || from.path !== path || from.dir !== m.dir || from.fixKey !== m.fixKey) {
      m.timeline = buildTimeline(m, path, fix.calls!)
      m.timelineFrom = { calls: fix.calls!, path, dir: m.dir, fixKey: m.fixKey }
    }
    if (m.timeline) {
      m.kind = kind
      const t = Math.min(nowSec, m.fixAt + NJ_BUS_ESTIMATE_MAX_AGE_S)
      // Never short of the prediction it took over from: at the hand-off the prediction may
      // already be past where the timetable would put the bus.
      const s = Math.max(distanceAt(m.timeline, t), predictedD(m, path, nowSec) * m.dir)
      return Math.max(0, Math.min(path.length, s * m.dir))
    }
  }
  m.kind = kind === 'live' ? 'live' : 'lastSighting'
  return predictedD(m, path, nowSec)
}

/**
 * The subway's follow (TransitMap's progress SmoothDamp, Unity's form): critically damped
 * toward `target`, speed capped by the change clamp, never overshooting and never backwards.
 * Forward metres in and out; `vel` is m/s.
 */
function smoothDampForward(cur: number, target: number, vel: number, dt: number): { pos: number; vel: number } {
  const omega = 2 / PROGRESS_SMOOTH_TIME
  const x = omega * dt
  const expf = 1 / (1 + x + 0.48 * x * x + 0.235 * x * x * x)
  const maxChange = MAX_CATCHUP_MPS * PROGRESS_SMOOTH_TIME
  const change = Math.max(-maxChange, Math.min(maxChange, cur - target))
  const temp = (vel + omega * change) * dt
  let nextVel = (vel - omega * temp) * expf
  let pos = cur - change + (change + temp) * expf
  if (pos > target) {
    pos = target
    nextVel = 0
  }
  return { pos: Math.max(cur, pos), vel: Math.max(0, nextVel) }
}

/** Set the drawn pose for `nowSec`. */
function settle(m: NjBusMotion, fix: NjBusFix, nowSec: number, dtSec: number, bearing: number): NjBusMotion {
  m.stale = nowSec - m.fixAt > NJ_BUS_STALE_S
  if (m.path) {
    const target = targetD(m, m.path, fix, nowSec)
    const gap = (target - m.d) * m.dir
    if (gap > 0) {
      if (dtSec > 0) {
        const next = smoothDampForward(m.d * m.dir, target * m.dir, m.vel, dtSec)
        m.d = next.pos * m.dir
        m.vel = next.vel
      }
    } else if (-gap > NJ_BUS_REWIND_M) {
      m.d = target
      m.vel = 0
    } else {
      // Drawn ahead of the target by less than NJ_BUS_REWIND_M — hold.
      m.vel = 0
    }
    const pt = pointAtDistance(m.path, m.d)
    m.lon = pt.lon
    m.lat = pt.lat
    m.heading = m.dir === 1 ? pt.bearing : (pt.bearing + 180) % 360
    return m
  }
  // Free glide: a fix off every line can't be held or estimated along anything.
  m.kind = njBusPositionKind(m.fixAt, nowSec, { layoverUntil: fix.layoverUntil, estimable: false })
  const span = m.t1 - m.t0
  const p = span > 0 ? Math.min(1, Math.max(0, (nowSec - m.t0) / span)) : 1
  m.lon = m.fromLon + (m.toLon - m.fromLon) * p
  m.lat = m.fromLat + (m.toLat - m.fromLat) * p
  const aim = metresBetween(m.lon, m.lat, m.toLon, m.toLat) > 5
    ? bearingDeg(m.lon, m.lat, m.toLon, m.toLat)
    : bearing
  const k = dtSec > 0 ? 1 - Math.exp(-dtSec / FREE_HEADING_TAU_S) : 1
  const turn = ((aim - m.heading + 540) % 360) - 180
  m.heading = (m.heading + turn * k + 360) % 360
  return m
}
